import './StripCalendar.scss';
import { useEffect, useState } from 'react';
import StripCalendarBody from './StripCalendarBody/StripCalendarBody';
import StripCalendarDates from './StripCalendarDates/StripCalendarDates';
import StripCalendarHolidayLabel from './StripCalendarHolidayLabel/StripCalendarHolidayLabel';
import StripCalendarSelectedEventBorderBottom from './StripCalendarSelectedEventBorderBottom/StripCalendarSelectedEventBorderBottom';
import StripCalendarTodayLabel from './StripCalendarTodayLabel/StripCalendarTodayLabel';
import { getEventDaySpan } from '../StandardCalendar/StandardCalendarHelper';
import StripCalendarEvent from '../StripCalendarEvent/StripCalendarEvent';
import { eventActivityType } from '../../constants/myBlockMonth';
import { convertToDateObject } from '../../helpers/dateHelpers';
import { CalendarDay, ActiveDate } from '../../models/MyBlockMonthInterface';
import helper from '../../pages/MyBlockMonth/intersectingElementsHelper';
import { checkEventCarryOut, getStripCalendarHeight } from '../../pages/MyBlockMonth/myBlockMonthHelper';
import { useMyBlockMonthProvider } from '../../providers/MyBlockMonthProvider';

const StripCalendar = ({actualView, eventList, holidayEventList, selectedEventIndex, setSelectedEventIndex, viewAll, noData}) => {
    const { checkIntersectingElementsSameClassNameWithResize } = helper(document);
    const myBlockMonthContext = useMyBlockMonthProvider();
    const [calendarElementWidth, setCalendarElementWidth] = useState(0);
    const [dates, setDates] = useState<CalendarDay[]>([]);
    const [stripCalHeightLevel, setStripCalHeightLevel] = useState(1);
    const [today, setToday] = useState({
        value: new Date(),
        index: -1
    });
    const blockDate = {
        start: convertToDateObject(myBlockMonthContext.myBlockMonthData.blockDates?.start, '00:00'),
        end: convertToDateObject(myBlockMonthContext.myBlockMonthData.blockDates?.end,'23:59')
    }
    const DISREGARDED_EVENT_TYPES_FOR_ACTIVE_DATE = [
        eventActivityType.GDO,
        eventActivityType.GDO6,
        eventActivityType.HOLIDAY,
        eventActivityType.SDO,
        eventActivityType.SICK,
        eventActivityType.SICK_INS,
        eventActivityType.TDO,
        eventActivityType.TDO6,
        eventActivityType.VACATION,
        eventActivityType.VAC2448
    ];
    const INITIAL_SELECTED_FIELDS = {
        arrowIndex: 0,
        arrowIndexMonth: 1,
        arrowPushLeft: false,
        borderBottomMarginLeft: '0%',
        borderBottomWidth: '0%'
    }
    const [selectedFields, setSelectedFields] = useState(INITIAL_SELECTED_FIELDS);

    useEffect(() => {
        getDaysInMonth();
        
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if(dates.length === 0) return;
        const eventsWithStripHeightLevel = eventList.filter(event => event.stripHeightLevel);
        if(eventsWithStripHeightLevel.length > 0) {
            const heightLevelThreeIndex = eventsWithStripHeightLevel.findIndex(event => event.stripHeightLevel === 3);
            if(heightLevelThreeIndex !== -1) setStripCalHeightLevel(3);
            else setStripCalHeightLevel(2);
        }
        else setStripCalHeightLevel(1);
        checkEventElementsEllipsis();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actualView, dates])

    /**
     * useEffect to handle which event should be selected
     */
    useEffect(() => {
        if(!calendarElementWidth || !eventList.length) return;
        if(noData) {
            setDates(dates.map(date => {
                return {
                    ...date,
                    active: false
                }
            }))
            return;
        }
        
        /* if viewAll is selected, show calendar with no elements selected */
        if(viewAll) {
            const selectedDates = [...dates];
            selectedDates.forEach(date => date.selected = false);
            setDates(selectedDates);
            setSelectedFields(INITIAL_SELECTED_FIELDS);
        } else {
            if(dates.length === 0) assignSelectedDates(getSelectedDatesIndex(eventList[selectedEventIndex], getDaysInMonth()), getDaysInMonth());
            else assignSelectedDates(getSelectedDatesIndex(eventList[selectedEventIndex]));
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEventIndex, viewAll, calendarElementWidth])

    /**
     * Set which dates are selected
     * @param selectedDateIndexes 
     * @param dates 
     */
     const assignSelectedDates = (selectedDateIndexes, datesList=dates) => {
        const selectedDates = [...datesList];
        selectedDates.forEach((datesList,index) => selectedDateIndexes.includes(index) ? datesList.selected = true : datesList.selected = false);
        setDates(selectedDates);
    }

    const checkEventElementsEllipsis = () => {
        checkIntersectingElementsSameClassNameWithResize('event__id', 'event__details-ellipsis');
        checkIntersectingElementsSameClassNameWithResize('event__check-in-time', 'event__details-ellipsis');
        checkIntersectingElementsSameClassNameWithResize('event__check-out-time', 'event__details-ellipsis');
    }

    /**
     * Check whether a carryOver event exists in the eventList and if so, add that day to the days array
     * @param days 
     * @param type (carry-in / carry-out)
     */
     const checkForCarryOverDate = (days, type) => {
        const carryOverIndex = type === 'carry-in' 
        ? eventList.findIndex(activity => convertToDateObject(activity.start_date, '00:00') < blockDate.start) 
        : eventList.findIndex(activity => checkEventCarryOut(blockDate.end, activity));

        if(carryOverIndex !== -1) {
            const carryOverDay = type === 'carry-in' ? getCarryOverDay('carry-in') : getCarryOverDay('carry-out');
            const dayProp = {
                active: !DISREGARDED_EVENT_TYPES_FOR_ACTIVE_DATE.includes(eventList[carryOverIndex].activityType),
                carryOver: true,
                month: carryOverDay[2],
                name: carryOverDay[0],
                number: carryOverDay[1],
                selected: false,
                year: carryOverDay[3]
            }
            if(type === 'carry-in') days.unshift(dayProp)
            else days.push(dayProp)
        }
    }

    /**
     * @param event 
     * @returns an array of the days the event falls under
     */
     const getActiveDates = (event) => {
        const eventStartDate = convertToDateObject(event.start_date, '00:00');
        const eventEndDate = convertToDateObject(event.end_date, '00:00');

        /* if the event is a carry-in, set the start date to the blockDate start */
        const actualStartDate = eventStartDate < blockDate.start ? new Date(+blockDate.start) : eventStartDate;

        /* if the event is a carry-out, set the end date to the blockDate end */
        const actualEndDate = eventEndDate > blockDate.end ? new Date(+blockDate.end) : eventEndDate;
        
        let activeDates: Array<ActiveDate> = [];
        for(let date = actualStartDate; date <= actualEndDate; date.setDate(date.getDate() + 1)) {
            activeDates.push({
                day: date.getDate(),
                month: date.getMonth()
            })
        }
        return activeDates; 
    }

    /**
     * Get the carry-over day, either carry-in or carry-out
     * @param type 
     * @returns [dayName, dayNumber, month] (M, 11, 04) for Monday, April 11
     */
     const getCarryOverDay = (type) => {
        let edgeDayArr: any = [];
        let easternDayEdgeDay = type === 'carry-in' ? new Date(+blockDate.start) : new Date(+blockDate.end);
        let edgeDay = type === 'carry-in' ? new Date(easternDayEdgeDay.setDate(easternDayEdgeDay.getDate() - 1)) : new Date(easternDayEdgeDay.setDate(easternDayEdgeDay.getDate() + 1));
        edgeDayArr.push(edgeDay.toLocaleString('default', { weekday: 'narrow' }));
        edgeDayArr.push(edgeDay.getDate());
        edgeDayArr.push(edgeDay.getMonth()+1)
        edgeDayArr.push(edgeDay.getFullYear())
        return edgeDayArr;
    }   

    /**
     * Get the days in the month and set the active days
     * @returns 
     */
    const getDaysInMonth = () => {
        const startDate = new Date(+blockDate.start);
        let days: Array<CalendarDay> = [];

        /* array to hold which days in the calendar are active (to highlight them dark blue) */
        let daysActive = [] as any;
        
        /* for each event, return the array of dates it spans */
        eventList.forEach(event => {
            if(!DISREGARDED_EVENT_TYPES_FOR_ACTIVE_DATE.includes(event.activityType)) daysActive = daysActive.concat(getActiveDates(event));
        })

        /* for each day within the blockmonth, add the day to the days array and check whether that day is in the daysActive array */
        for(let day = startDate; day <= blockDate.end; day.setDate(day.getDate() + 1)) {
            days.push({
                active: daysActive.some(activeDay=> activeDay.day === day.getDate() && activeDay.month === day.getMonth()),
                name: day.toLocaleString('en-CA', {weekday:'narrow'}),
                month: day.getMonth()+1,
                number: day.getDate(),
                selected: false,
                today: today.value.getDate() === day.getDate() && today.value.getMonth() === day.getMonth() && today.value.getFullYear() === day.getFullYear()
            })
        }
        checkForCarryOverDate(days, 'carry-in');
        checkForCarryOverDate(days, 'carry-out');

        /* set the today label in the strip calendar */ 
        setToday({
            ...today,
            index: days.findIndex(day => day.today)
        })

        /* set each individual date width percentage */
        setCalendarElementWidth(100/days.length);

        setDates(days);
        return days;
    }

    /**
     * 
     * @param event 
     * @param datesList 
     * @returns the indexes of the days the event runs on, to highlight selected dates
     */
    const getSelectedDatesIndex = (event, datesList=dates) => {
        let startDate = convertToDateObject(event.start_date, '00:00');
        let endDate = convertToDateObject(event.end_date, '23:59');

        /* if the event is a carryIn, set the startDate to the blockmonth startDate */
        if(startDate < blockDate.start) {
            startDate = new Date(+blockDate.start);
            startDate.setDate(startDate.getDate() - 1);
        }

        /* if the event is a carryOut, set the endDate to the blockmonth endDate */
        if(endDate > blockDate.end) {
            endDate = new Date(+blockDate.end);
            endDate.setDate(endDate.getDate() + 1);
        } 
        /* if the checkout time is 00:00, subtract a day*/
        if(event.checkOut.value === 0) {
            endDate.setDate(endDate.getDate() - 1);
        } 
        
        const startDay = startDate.getDate();
        const startMonth = startDate.getMonth()+1;

        /* find the index of the startDate on the strip calendar */
        const startIndex = datesList.findIndex(date => date.number === startDay && date.month === startMonth);
        const days = getEventDaySpan(startDate.getTime(), endDate.getTime());

        let result = [] as any;
        for(let i=startIndex; i<startIndex+days; i++) result.push(i);

        setSelectedFields({
            arrowIndex: new Date((startDate.getTime() + endDate.getTime()) / 2).getDate(),
            arrowIndexMonth: new Date((startDate.getTime() + endDate.getTime()) / 2).getMonth() + 1,
            arrowPushLeft: (days % 2) - 1 === 0 ? false : true,
            borderBottomMarginLeft: `${startIndex * calendarElementWidth}%`,
            borderBottomWidth: `${days * calendarElementWidth}%`
        })

        return result; 
    }
   
    const handleEventClick = (unique_id) => {
        const index = eventList.findIndex(event => event.unique_id === unique_id);
        setSelectedEventIndex(index);
        const mbmEventDetailViewContainer = document.getElementById(viewAll ? `event-details__${unique_id}`: 'mbm_events_view__container');
        if(mbmEventDetailViewContainer) mbmEventDetailViewContainer?.scrollIntoView({behavior: 'smooth'});
    }

    function windowResize() {
        checkEventElementsEllipsis();
    }
     
    window.addEventListener('resize', windowResize);

    return (
        <div className="c-strip-calendar">
            <StripCalendarDates dates={dates} />
            <div className="c-body" style={{height: `${getStripCalendarHeight(stripCalHeightLevel)}rem`}}>
                <div className="c-body__inner">
                    <StripCalendarBody dates={dates} selectedFields={selectedFields} />
                    {!noData && eventList.length && eventList.map((event, index) => {
                        return (
                            <StripCalendarEvent  
                                actualView={actualView} 
                                blockDate={blockDate} 
                                calendarElementWidth={calendarElementWidth} 
                                dates={dates} 
                                event={event}
                                handleEventClick={handleEventClick}
                                key={index}
                            />
                        )
                    })}
                    {holidayEventList.map((event, index) => {
                        return (
                            <StripCalendarHolidayLabel 
                                calendarElementWidth={calendarElementWidth} 
                                dates={dates} 
                                event={event} 
                                noData={noData}
                                key={index}
                            />
                        )
                    })}
                    <StripCalendarSelectedEventBorderBottom selectedFields={selectedFields} stripCalHeightLevel={stripCalHeightLevel} />
                    {today.index !== -1 && <StripCalendarTodayLabel calendarElementWidth={calendarElementWidth} today={today} />}
                </div>
            </div>
        </div>
    )
}

export default StripCalendar;